import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  tenantId: string | null;
  tenantSlug: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const extractTenantClaims = (session: Session | null) => ({
  tenantId:   (session?.user?.app_metadata?.tenant_id   as string) ?? null,
  tenantSlug: (session?.user?.app_metadata?.tenant_slug as string) ?? null,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser]             = useState<User | null>(null);
  const [session, setSession]       = useState<Session | null>(null);
  const [loading, setLoading]       = useState(true);
  const [tenantId, setTenantId]     = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);

  const applySession = (s: Session | null) => {
    setSession(s);
    setUser(s?.user ?? null);
    const { tenantId: tid, tenantSlug: tslug } = extractTenantClaims(s);
    setTenantId(tid);
    setTenantSlug(tslug);
    Sentry.setUser(
      s?.user ? { id: s.user.id, email: s.user.email, tenantId: tid ?? undefined } : null
    );
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        applySession(session);
        setLoading(false);
        // Recovery links land on SITE_URL (app root) with the session in the
        // URL hash; supabase-js consumes it and fires PASSWORD_RECOVERY. Send
        // the user to the set-a-new-password screen. replace() so the reload
        // picks up the just-persisted session and the hash is gone.
        if (_event === 'PASSWORD_RECOVERY' && window.location.pathname !== '/auth/reset-password') {
          window.location.replace(`${window.location.origin}/auth/reset-password`);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/onboarding`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setSession(null);
      setUser(null);
      setTenantId(null);
      setTenantSlug(null);
      Sentry.setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, tenantId, tenantSlug, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
